import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cachedGet } from '../../lib/api-client';
import { Spinner } from '../../components/kit/Spinner';
import { ErrorState } from '../../components/kit/ErrorState';
import { EmptyState } from '../../components/kit/EmptyState';
import { PushEnableCard } from '../../components/today/PushEnableCard';
import { formatISTTime } from '../../lib/format';
import { haptic } from '../../lib/haptic';
import { onTripRefresh } from '../../lib/trip-refresh';

type Trip = {
  id: string;
  code?: string | null;
  pickupTime: string;
  pickupLocation?: string | null;
  dropLocation?: string | null;
  status: string;
  client?: { name: string } | null;
};

type Tab = 'upcoming' | 'completed';

// Upper-cased booking statuses that count as "still has work to do today".
// Anything else is treated as completed/closed.
const OPEN_STATUSES = new Set(['ALLOCATED', 'DISPATCHED', 'ONGOING']);

export function TodayRoute() {
  const { t } = useTranslation('trip');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [tab, setTab] = useState<Tab>('upcoming');

  const load = async (forceNetwork = false) => {
    setError(false); setLoading(true);
    try {
      const result = await cachedGet<{ items: Trip[] }>('/driver-app/me/trips/today', { forceNetwork });
      setTrips(result.data.items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') void load(true); };
    const unsubscribe = onTripRefresh(refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 15_000);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, []);

  const { upcoming, completed, totalCount, doneCount } = useMemo(() => {
    const up: Trip[] = [];
    const dn: Trip[] = [];
    for (const tr of trips) {
      if (OPEN_STATUSES.has((tr.status || '').toUpperCase())) up.push(tr);
      else dn.push(tr);
    }
    return { upcoming: up, completed: dn, totalCount: trips.length, doneCount: dn.length };
  }, [trips]);

  if (loading && trips.length === 0) {
    return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  }
  if (error && trips.length === 0) return <ErrorState onRetry={load} />;

  const leftCount = totalCount - doneCount;
  const pctDone = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const today = new Date();
  const todayMeta = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short',
  }).format(today);

  return (
    <div className="px-5 pt-2 pb-10">
      <PushEnableCard />
      {/* Progress card — mint-soft surface with editorial italic numeral.
          Matches v2 today-v2.html spec exactly: "{N} of {total} trips left today". */}
      <div
        className="mt-2"
        style={{
          background: 'var(--mint-soft)',
          border: '1px solid var(--mint-line)',
          borderRadius: 'var(--radius-card)',
          padding: '16px 18px',
        }}
      >
        <div
          className="font-bold"
          style={{ fontSize: 15, color: 'var(--color-text)', display: 'flex', alignItems: 'baseline', gap: 6 }}
        >
          <span className="serif-accent" style={{ fontSize: 28, color: 'var(--green-700)', lineHeight: 1 }}>
            {leftCount}
          </span>
          <span>
            {t('today.progress', {
              defaultValue: 'of {{total}} trips left today',
              total: totalCount,
            })}
          </span>
        </div>
        <div
          style={{
            marginTop: 12,
            height: 6,
            borderRadius: 999,
            background: 'rgba(46, 158, 101, 0.18)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pctDone}%`,
              height: '100%',
              background: 'var(--color-primary)',
              borderRadius: 999,
              transition: 'width 400ms ease',
            }}
          />
        </div>
        <div
          className="font-semibold uppercase"
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'var(--green-700)',
            letterSpacing: '0.08em',
          }}
        >
          {t('today.meta', { defaultValue: '{{done}} done · {{date}}', done: doneCount, date: todayMeta })}
        </div>
      </div>

      {/* Sticky segmented tabs — pinned to the top of the scroll area so only
          the list below scrolls (the progress card above scrolls away under
          them). -mx-5 px-5 spans the bg full-width; bg-page hides content. */}
      <div className="sticky top-0 z-20 -mx-5 px-5 pt-2 pb-3 mt-5 bg-[var(--color-bg-page)]">
        <div
          role="tablist"
          aria-label={t('today.title', { defaultValue: "Today's schedule" })}
          className="grid grid-cols-2 gap-1 p-1 rounded-[12px] border border-[var(--color-border)]"
          style={{ background: 'rgba(0, 0, 0, 0.035)' }}
        >
          <TabButton
            active={tab === 'upcoming'}
            count={upcoming.length}
            onClick={() => { if (tab !== 'upcoming') { haptic.tap(); setTab('upcoming'); } }}
          >
            {t('tabs.upcoming')}
          </TabButton>
          <TabButton
            active={tab === 'completed'}
            count={completed.length}
            onClick={() => { if (tab !== 'completed') { haptic.tap(); setTab('completed'); } }}
          >
            {t('tabs.completed')}
          </TabButton>
        </div>
      </div>

      <div className="mt-2">
        {tab === 'upcoming' ? (
          upcoming.length === 0
            ? <EmptyState title={t('empty.upcoming')} />
            : <TripList items={upcoming} variant="upcoming" />
        ) : (
          completed.length === 0
            ? <EmptyState title={t('empty.completed')} />
            : <TripList items={completed} variant="done" />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active, count, onClick, children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 h-[38px] rounded-[9px] text-sm transition-colors"
      style={{
        background: active ? 'var(--color-surface)' : 'transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-secondary)',
        fontWeight: active ? 700 : 600,
        boxShadow: active
          ? '0 1px 3px rgba(15, 59, 38, 0.08), 0 1px 2px rgba(15, 59, 38, 0.04)'
          : 'none',
      }}
    >
      <span>{children}</span>
      <span
        className="font-bold"
        style={{
          fontSize: 11,
          background: active ? 'var(--mint-soft)' : 'rgba(0, 0, 0, 0.06)',
          color: active ? 'var(--green-700)' : 'var(--color-text-muted,#6B7480)',
          padding: '1px 7px',
          borderRadius: 999,
          lineHeight: 1.4,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function TripList({ items, variant }: { items: Trip[]; variant: 'upcoming' | 'done' }) {
  return (
    <ul className="space-y-2.5">
      {items.map((trip, idx) => (
        <TripRow key={trip.id} trip={trip} variant={variant} isNext={variant === 'upcoming' && idx === 0} />
      ))}
    </ul>
  );
}

function TripRow({ trip, variant, isNext }: { trip: Trip; variant: 'upcoming' | 'done'; isNext: boolean }) {
  const done = variant === 'done';
  const time = formatISTTime(trip.pickupTime);
  return (
    <li>
      <Link
        to={`/trips/${trip.code || trip.id}`}
        className="block"
        style={{
          background: done ? 'var(--color-surface-soft)' : 'var(--color-surface)',
          border: `1px solid ${isNext ? 'var(--mint-line)' : 'var(--color-border)'}`,
          borderRadius: 'var(--radius-row, 14px)',
          padding: '14px 16px',
          boxShadow: isNext
            ? '0 14px 32px rgba(15, 59, 38, 0.08), 0 3px 8px rgba(15, 59, 38, 0.05)'
            : 'none',
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
        }}
      >
        <div className="flex items-center gap-2.5 mb-2">
          {done ? (
            <Pill tone="success" withDot>Done</Pill>
          ) : null}
          <span
            className="font-extrabold"
            style={{
              fontSize: 16,
              letterSpacing: '-0.01em',
              color: done ? 'var(--color-text-muted,#6B7480)' : 'var(--color-text)',
              fontWeight: done ? 700 : 800,
            }}
          >
            {time}
          </span>
          {isNext ? <Pill tone="success" withDot>Next</Pill> : null}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: done ? 700 : 800,
            letterSpacing: '-0.01em',
            color: done ? 'var(--color-text-secondary)' : 'var(--color-text)',
          }}
        >
          {trip.client?.name || '—'}
        </div>
        {(() => {
          const hasPickupAddr = !!(trip.pickupLocation && trip.pickupLocation.trim());
          const hasDropAddr = !!(trip.dropLocation && trip.dropLocation.trim());
          if (!hasPickupAddr && !hasDropAddr) return null;
          return (
            <div
              className="flex items-center gap-1.5 mt-2 min-w-0"
              style={{
                fontSize: 13,
                fontWeight: done ? 500 : 600,
                color: done ? 'var(--color-text-muted,#6B7480)' : 'var(--color-text)',
              }}
            >
              <span className="flex-1 min-w-0 truncate">
                {trip.pickupLocation || '—'}
              </span>
              {hasDropAddr ? (
                <>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    style={{ color: 'var(--color-text-dim, #9AA3AC)', flexShrink: 0 }}
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                  <span className="flex-1 min-w-0 truncate">{trip.dropLocation}</span>
                </>
              ) : null}
            </div>
          );
        })()}
      </Link>
    </li>
  );
}

function Pill({ tone, withDot, children }: { tone: 'success' | 'warn' | 'muted'; withDot?: boolean; children: React.ReactNode }) {
  const palette = tone === 'success'
    ? { bg: 'var(--mint-soft)', fg: 'var(--green-700)', border: 'var(--mint-line)' }
    : tone === 'warn'
      ? { bg: 'var(--color-warn-bg,#FBF1DE)', fg: 'var(--color-warn-fg,#8A5E1D)', border: 'rgba(197,142,58,0.35)' }
      : { bg: '#F1EFE5', fg: 'var(--color-text-muted,#6B7480)', border: 'var(--color-border)' };
  return (
    <span
      className="inline-flex items-center gap-1.5 font-bold uppercase"
      style={{
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        padding: '3px 9px',
        borderRadius: 'var(--radius-pill, 9999px)',
        fontSize: 11,
        letterSpacing: '0.04em',
        lineHeight: 1.4,
      }}
    >
      {withDot ? (
        <span
          aria-hidden
          style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }}
        />
      ) : null}
      {children}
    </span>
  );
}
